import { useMemo } from 'react';
import { Outlet } from 'react-router-dom';
import { createTheme, StyledEngineProvider, ThemeProvider, useTheme } from '@mui/material/styles';
import GlobalStyles from '@mui/material/GlobalStyles';
import type {} from '@mui/x-data-grid-premium/themeAugmentation';
import { useMe } from '@/api/auth/auth.hooks';
import { PERMISSIONS } from '@/constants/permissions';
import { IfPermitted } from '@/modules/internal-v2/shared/if-permitted.component';
import { Topbar } from './topbar.component';
import { AssistantPanel } from '../assistant/assistant-panel.component';
import { AnnouncementsHost } from '@components/announcements/announcements-host.component';

import '@fontsource/inter/400.css';
import '@fontsource/inter/500.css';
import '@fontsource/inter/600.css';
import '@fontsource/inter/700.css';
import '@fontsource/jetbrains-mono/400.css';
import '@fontsource/jetbrains-mono/500.css';
import '@fontsource/jetbrains-mono/600.css';

import '@/modules/internal-v2/ds/styles/index.css';
import '../styles/shell.css';
import '../styles/datagrid-override.css';
import '../assistant/assistant.css';

export function AppShell() {
  // Refresh the authorization snapshot whenever the user enters the internal
  // area. Without this, a deep-link to `/i/...` on a fresh session reads an
  // empty persisted Zustand store and bounces the user to /auth or shows 403
  // even when they have valid access.
  useMe();
  const parentTheme = useTheme();

  const dsTheme = useMemo(
    () =>
      createTheme(parentTheme, {
        palette: {
          DataGrid: {
            bg: '#ffffff',
            headerBg: '#f0f1f3',
            pinnedBg: '#f0f1f3',
          },
        },
      }),
    [parentTheme],
  );

  return (
    <StyledEngineProvider enableCssLayer>
      <GlobalStyles styles="@layer theme, base, mui, components, utilities;" />
      <ThemeProvider theme={dsTheme}>
        <div className="v2-shell" data-ds="eos">
          <Topbar />
          <div className="v2-content-frame">
            <AnnouncementsHost
              placement="INTERNAL_ADMIN"
              className="eos-ann-internal-pad"
            />
            <Outlet />
          </div>
          <IfPermitted permission={PERMISSIONS.AI_ASSISTANT_VIEW}>
            <AssistantPanel />
          </IfPermitted>
        </div>
      </ThemeProvider>
    </StyledEngineProvider>
  );
}
